import { Crown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useLanguage } from "@/contexts/language";
import { t } from "@/utils/translations";

const Footer = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const { language } = useLanguage();
  
  return (
    <footer className="bg-background py-8 md:py-12 border-t w-full overflow-x-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
          <div className="sm:col-span-2">
            <h3 className="text-lg font-semibold mb-3 md:mb-4">LittleAI Diary</h3>
            <p className="text-muted-foreground text-sm md:text-base mb-4 max-w-md">
              {t('footer.description', language).replace('BabyDiary', 'LittleAI Diary')}
            </p>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/premium')}
              className="flex items-center gap-1"
            >
              <Crown className="h-3.5 w-3.5 text-amber-500" />
              {t('footer.upgradeToPremium', language)}
            </Button>
          </div>
          
          <div>
            <h4 className="font-medium mb-2 md:mb-3">{t('footer.features', language)}</h4>
            <ul className="space-y-1 md:space-y-2">
              <li>
                <Button 
                  variant="link" 
                  className="p-0 h-auto text-sm text-muted-foreground hover:text-foreground"
                  onClick={() => navigate('/generate')}
                >
                  {t('footer.createEntries', language)}
                </Button>
              </li>
              <li>
                <Button 
                  variant="link" 
                  className="p-0 h-auto text-sm text-muted-foreground hover:text-foreground"
                  onClick={() => navigate('/entries')}
                >
                  {t('footer.viewEntries', language)}
                </Button>
              </li>
              <li>
                <Button 
                  variant="link" 
                  className="p-0 h-auto text-sm text-muted-foreground hover:text-foreground"
                  onClick={() => navigate('/statistics')}
                >
                  {t('footer.growthStatistics', language)}
                </Button>
              </li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-medium mb-2 md:mb-3">{t('footer.account', language)}</h4>
            <ul className="space-y-1 md:space-y-2">
              <li>
                <Button 
                  variant="link" 
                  className="p-0 h-auto text-sm text-muted-foreground hover:text-foreground"
                  onClick={() => navigate('/login')}
                >
                  {t('footer.login', language)}
                </Button>
              </li>
              <li>
                <Button 
                  variant="link" 
                  className="p-0 h-auto text-sm text-muted-foreground hover:text-foreground"
                  onClick={() => navigate('/signup')}
                >
                  {t('footer.signUp', language)}
                </Button>
              </li>
              <li>
                <Button 
                  variant="link" 
                  className="p-0 h-auto text-sm text-muted-foreground hover:text-foreground"
                  onClick={() => navigate('/premium')}
                >
                  {t('footer.premiumPlan', language)}
                </Button>
              </li>
            </ul>
          </div>
        </div>
        
        <Separator className="my-6 md:my-8" />
        
        <div className="flex flex-col sm:flex-row justify-between items-center">
          <p className="text-xs sm:text-sm text-muted-foreground">
            &copy; {currentYear} LittleAI Diary. {t('footer.copyright', language)}
          </p>
          <div className="flex gap-2 sm:gap-4 mt-4 sm:mt-0 flex-wrap justify-center">
            <Button variant="ghost" size="sm" className="text-xs h-8">{t('footer.privacy', language)}</Button>
            <Button variant="ghost" size="sm" className="text-xs h-8">{t('footer.terms', language)}</Button>
            <Button variant="ghost" size="sm" className="text-xs h-8">{t('footer.contact', language)}</Button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
